import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Link,
  Chip,
  Grid,
  TextField
} from '@mui/material';
import {
  LocalShipping as ShippingIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { shipmentService } from '../../services/shipmentService';

interface ShipmentSectionProps {
  orderId: string;
  awbNumber?: string;
  trackingUrl?: string;
  shipmentStatus?: string;
  carrierName?: string;
  onShipmentCreated?: () => void;
}

const ShipmentSection: React.FC<ShipmentSectionProps> = ({
  orderId,
  awbNumber,
  trackingUrl,
  shipmentStatus,
  carrierName,
  onShipmentCreated
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [warehouse, setWarehouse] = useState<'NOIDA' | 'NIMIYA'>('NOIDA');
  const [weight, setWeight] = useState<string>('1.0');
  const [length, setLength] = useState<string>('30');
  const [width, setWidth] = useState<string>('20');
  const [height, setHeight] = useState<string>('15');
  const [loading, setLoading] = useState(false);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [labelUrl, setLabelUrl] = useState<string | null>(null);

  const handleCreateShipment = async () => {
    try {
      setLoading(true);
      
      // Validate inputs
      const weightNum = parseFloat(weight);
      const lengthNum = parseFloat(length);
      const widthNum = parseFloat(width);
      const heightNum = parseFloat(height);
      
      if (isNaN(weightNum) || weightNum <= 0) {
        enqueueSnackbar('Please enter a valid weight', { variant: 'error' });
        return;
      }
      
      if (isNaN(lengthNum) || lengthNum <= 0 || isNaN(widthNum) || widthNum <= 0 || isNaN(heightNum) || heightNum <= 0) {
        enqueueSnackbar('Please enter valid dimensions', { variant: 'error' });
        return;
      }
      
      const result = await shipmentService.createShipment(orderId, {
        warehouse_name: warehouse,
        weight: weightNum,
        length: lengthNum,
        width: widthNum,
        height: heightNum
      });

      enqueueSnackbar(result.message || 'Shipment created successfully', { variant: 'success' });
      
      // Store label URL
      if (result.label_url) {
        setLabelUrl(result.label_url);
      }

      // Notify parent to refresh order data
      if (onShipmentCreated) {
        onShipmentCreated();
      }
    } catch (error: any) {
      console.error('Failed to create shipment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create shipment';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTrackShipment = async () => {
    try {
      setTrackingLoading(true);
      await shipmentService.trackShipment(orderId);
      
      enqueueSnackbar('Tracking information updated', { variant: 'success' });
      
      // Notify parent to refresh order data
      if (onShipmentCreated) {
        onShipmentCreated();
      }
    } catch (error: any) {
      console.error('Failed to track shipment:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to track shipment';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setTrackingLoading(false);
    }
  };

  const handleDownloadLabel = async () => {
    try {
      if (labelUrl) {
        window.open(labelUrl, '_blank');
        return;
      }

      const result = await shipmentService.getShipmentLabel(orderId);
      
      if (result.label_url) {
        setLabelUrl(result.label_url);
        window.open(result.label_url, '_blank');
      }
    } catch (error: any) {
      console.error('Failed to get label:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to get shipping label';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    }
  };

  // If shipment already exists, show tracking info
  if (awbNumber) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <ShippingIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Shipment Information</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                AWB Number
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {awbNumber}
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Carrier
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {carrierName || 'Delhivery'}
              </Typography>
            </Grid>

            {shipmentStatus && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Chip 
                  label={shipmentStatus} 
                  color="primary" 
                  size="small" 
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            )}

            {trackingUrl && (
              <Grid item xs={12}>
                <Link 
                  href={trackingUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ display: 'flex', alignItems: 'center', mt: 1 }}
                >
                  <QrCodeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                  Track Shipment
                </Link>
              </Grid>
            )}
          </Grid>

          <Box display="flex" gap={1} mt={3}>
            <Button
              variant="outlined"
              startIcon={trackingLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleTrackShipment}
              disabled={trackingLoading}
              size="small"
            >
              Refresh Tracking
            </Button>

            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handleDownloadLabel}
              size="small"
            >
              Download Label
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // If no shipment, show create shipment form
  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" mb={2}>
          <ShippingIcon sx={{ mr: 1 }} />
          <Typography variant="h6">Create Shipment</Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          Create a shipment to generate AWB number and shipping label for this order.
        </Alert>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Warehouse</InputLabel>
          <Select
            value={warehouse}
            label="Warehouse"
            onChange={(e) => setWarehouse(e.target.value as 'NOIDA' | 'NIMIYA')}
            disabled={loading}
          >
            <MenuItem value="NOIDA">NOIDA - Noida, Uttar Pradesh</MenuItem>
            <MenuItem value="NIMIYA">NIMIYA - Daltonganj, Jharkhand</MenuItem>
          </Select>
        </FormControl>

        <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
          Package Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Weight (kg)"
              type="number"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              disabled={loading}
              inputProps={{ min: 0.1, step: 0.1 }}
              helperText="Enter package weight in kilograms"
            />
          </Grid>
          
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Length (cm)"
              type="number"
              value={length}
              onChange={(e) => setLength(e.target.value)}
              disabled={loading}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
          
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Width (cm)"
              type="number"
              value={width}
              onChange={(e) => setWidth(e.target.value)}
              disabled={loading}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
          
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Height (cm)"
              type="number"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              disabled={loading}
              inputProps={{ min: 1, step: 1 }}
            />
          </Grid>
        </Grid>

        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={16} /> : <ShippingIcon />}
          onClick={handleCreateShipment}
          disabled={loading}
          fullWidth
        >
          {loading ? 'Creating Shipment...' : 'Create Shipment'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ShipmentSection;
